/**
 * Turn a restaurant name into a slug the server will accept.
 *
 * The server's shape is `^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$` (domain/model Slug), and it
 * REJECTS anything else outright rather than cleaning it up — `café` is a documented rejection.
 * Since signup derives the slug from a name the owner typed, accents are not an edge case here,
 * they are the common case in French. Hence the decomposition + diacritic strip rather than a
 * plain lowercase.
 *
 * Slugs are claimed platform-wide, first-writer-wins, so a collision on "chez-marie" is expected
 * rather than exceptional — [slugCandidates] supplies the numbered fallbacks the caller retries
 * with. The server also offers richer suggestions (`le-`, `-paris`, …) via its slug policy; those
 * are for a human choosing deliberately, not for an automatic first attempt.
 */
const MAX_LENGTH = 63;

export function slugify(name: string): string {
  const base = name
    .normalize("NFD") // é -> e + combining acute
    .replace(/[\u0300-\u036f]/g, "") // drop the combining marks
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-") // everything else becomes a separator
    .replace(/-+/g, "-") // collapse runs
    .replace(/^-|-$/g, ""); // the shape forbids leading/trailing dashes
  return base.slice(0, MAX_LENGTH).replace(/-$/, "");
}

/**
 * The slug to try, then the fallbacks if it is taken. Truncation accounts for the suffix, so a
 * 63-character name does not produce a 65-character candidate that fails the shape check instead
 * of the uniqueness one — a confusing way to lose.
 */
export function slugCandidates(name: string, attempts = 5): string[] {
  const base = slugify(name);
  if (base === "") return [];
  return Array.from({ length: attempts }, (_, i) => {
    if (i === 0) return base;
    const suffix = `-${i + 1}`;
    return `${base.slice(0, MAX_LENGTH - suffix.length).replace(/-$/, "")}${suffix}`;
  });
}
